<script context="module" lang="ts">
  export const key = 'careersFilters';
  const closeAt = 960;
</script>

<script lang="ts">
  import type { JobFilters } from '$lib/careers/types';
  import { cx } from '@emotion/css';
  import { Filter, CloseX } from '$components/Icons';
  import Modal from '$components/Modals/Modal.svelte';
  import { modals } from '$lib/stores/modals';
  import { ResponsiveVars, TwistBox, TwistStack, TwistDivider } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import FilterGroup from './FilterGroup.svelte';
  import JobCount from './JobCount.svelte';
  import BusinessUnits from './BusinessUnits.svelte';
  import ResetFiltersButton from './ResetFiltersButton.svelte';

  export let values: JobFilters;

  const responsiveVars = new ResponsiveVars()
    .addFluid('toggle-padding', toFluidVarInput(12, 12))
    .addFluid('toggle-font-size', toFluidVarInput(12, 12))
    .addFluid('toggle-icon-size', toFluidVarInput(24, 24))
    .addFluid('toggle-icon-mr', toFluidVarInput(9, 9))
    .addFluid('divider-my', toFluidVarInput(24, 24))
    .toCSS();
</script>

<div class={cx(responsiveVars, 'mobile-filters')}>
  <TwistStack direction="row" justify="space-between" align="center">
    <button class="mobile-filters__toggle" on:click={() => modals.open('careersFilters')}>
      <Filter />
      Filters
    </button>
    <JobCount />
  </TwistStack>

  <Modal
    modalKey={key}
    autoCloseAt={closeAt}
    ariaLabel="Filters"
    size="fullscreen"
    let:onCloseClicked={handleCloseClicked}
  >
    <div class={cx(responsiveVars, 'container')}>
      <TwistStack direction="column" align="stretch">
        <TwistBox marginY={toFluidVarInput(12, 12)}>
          <TwistStack direction="row" justify="space-between" align="center">
            <button class="mobile-filters__toggle" on:click={handleCloseClicked}>
              <CloseX />
              Hide Filters
            </button>
            <JobCount />
          </TwistStack>
        </TwistBox>
        <FilterGroup filterValues={values} on:filters-change />
      </TwistStack>
      <span class="divider">
        <TwistDivider color="gray-100" thickness="1px" />
      </span>
      <TwistStack direction="column" align="center">
        <BusinessUnits filterValues={values.businessUnits} on:filters-change />
        <ResetFiltersButton on:filters-change />
      </TwistStack>
    </div>
  </Modal>
</div>

<style lang="scss">
  @import 'src/scss/media';

  .container {
    height: 100%;
    overflow-y: scroll;
    position: sticky;
    background-color: var(--twist-color-gray-10);
  }

  .mobile-filters {
    @include at('lg') {
      display: none;
    }
  }

  .mobile-filters__toggle {
    display: flex;
    align-items: center;
    padding: var(--toggle-padding);
    background-color: var(--twist-color-gray-0);
    border: none;
    border-radius: 1000px;
    font-family: var(--font-family-primary);
    font-size: var(--toggle-font-size);
    font-weight: 700;
    line-height: 125%;
    letter-spacing: 0.9px;
    text-transform: uppercase;

    :global(svg) {
      height: var(--toggle-icon-size);
      width: var(--toggle-icon-size);
      margin-right: var(--toggle-icon-mr);
    }

    &:hover,
    &:focus,
    &:focus-visible {
      color: var(--twist-color-gray-0);
      background-color: var(--twist-color-gray-100);
      outline: none;
    }

    &:active {
      opacity: 50%;
    }
  }

  .divider {
    opacity: 30%;
    margin: var(-divider-my) 0;
  }
</style>
